const API_BASE_URL = (
  process.env.REACT_APP_ADMIN_API_BASE_URL ||
  process.env.REACT_APP_API_BASE_URL ||
  "https://api.dwelloradesigns.com"
).replace(/\/$/, "");

const ADMIN_TOKEN_KEY = "dwellora_admin_token";

export function getAdminToken() {
  return window.sessionStorage.getItem(ADMIN_TOKEN_KEY);
}

export function adminMediaUrl(url) {
  if (!url || /^https?:\/\//i.test(url)) return url || "";
  return API_BASE_URL + url;
}

export function isAdminAuthenticated() {
  return Boolean(getAdminToken());
}

export function clearAdminSession() {
  window.sessionStorage.removeItem(ADMIN_TOKEN_KEY);
}

async function requestAdminApi(path, options = {}) {
  const token = getAdminToken();
  const headers = {
    Accept: "application/json",
    ...(options.body ? { "Content-Type": "application/json" } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(options.headers || {}),
  };

  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: options.method || "GET",
    headers,
    body: options.body,
  });

  let payload;
  try {
    payload = await response.json();
  } catch (error) {
    payload = null;
  }

  if (!response.ok) {
    if (response.status === 401) {
      clearAdminSession();
    }
    throw new Error(payload?.message || "Unable to load admin data.");
  }

  return payload;
}

export async function loginAdmin({ username, password }) {
  const payload = await requestAdminApi("/api/admin/login", {
    method: "POST",
    body: JSON.stringify({ username, password }),
  });

  if (!payload?.token) {
    throw new Error("Login failed. Please try again.");
  }

  window.sessionStorage.setItem(ADMIN_TOKEN_KEY, payload.token);
  return payload.admin || { username };
}

export async function getAdminStats() {
  const payload = await requestAdminApi("/api/admin/stats");
  return payload?.stats || {
    totalEnquiries: 0,
    contactEnquiries: 0,
    designerEnquiries: 0,
    meetingBookings: 0,
    totalProducts: 0,
    activeProducts: 0,
    totalQuotations: 0,
    draftQuotations: 0,
  };
}

export async function getCatalogueCategories() {
  const payload = await requestAdminApi("/api/categories");
  return Array.isArray(payload?.data) ? payload.data : [];
}

export async function getCatalogueProducts() {
  const payload = await requestAdminApi("/api/products");
  return Array.isArray(payload?.data) ? payload.data : [];
}

export async function getPlannerConfig() {
  const payload = await requestAdminApi("/api/planner/config/admin");
  return payload?.data || { rates: [], bindings: [], rooms: [] };
}

export function savePlannerRate(rate) {
  return requestAdminApi(`/api/planner/rates/${encodeURIComponent(rate.id)}`, { method: "PUT", body: JSON.stringify(rate) });
}

export async function uploadPlannerRateImage(id, image) {
  const data = new FormData();
  data.append("image", image);
  const response = await fetch(`${API_BASE_URL}/api/planner/rates/${encodeURIComponent(id)}/image`, {
    method: "POST",
    headers: { Authorization: `Bearer ${getAdminToken()}` },
    body: data,
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok) throw new Error(payload?.message || "Unable to upload planner option image.");
  return payload;
}

export function savePlannerOption({ productId, categoryId, enabled, questionKey, roomTypes }) {
  return requestAdminApi("/api/planner/options", {
    method: "POST",
    body: JSON.stringify({
      product_id: productId,
      category_id: categoryId,
      enabled,
      question_key: questionKey,
      room_types: roomTypes,
    }),
  });
}

export function saveCatalogueProduct(product) {
  return requestAdminApi(product.id ? `/api/products/${product.id}` : "/api/products", {
    method: product.id ? "PUT" : "POST", body: JSON.stringify(product),
  });
}

export async function uploadCatalogueImage(id, image) {
  const data = new FormData(); data.append("image", image);
  const response = await fetch(`${API_BASE_URL}/api/products/${encodeURIComponent(id)}/image`, {
    method: "POST", headers: { Authorization: `Bearer ${getAdminToken()}` }, body: data,
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok) throw new Error(payload?.message || "Unable to upload image.");
  return payload;
}

export async function getContactEnquiries() {
  const payload = await requestAdminApi("/api/admin/contact-enquiries");
  return Array.isArray(payload?.data) ? payload.data : [];
}

export async function getDesignerEnquiries() {
  const payload = await requestAdminApi("/api/admin/designer-enquiries");
  return Array.isArray(payload?.data) ? payload.data : [];
}

export async function getMeetingBookings() {
  const payload = await requestAdminApi("/api/admin/meetings");
  return Array.isArray(payload?.data) ? payload.data : [];
}

export async function getFurnitureEnquiries() {
  const payload = await requestAdminApi("/api/admin/furniture-enquiries");
  return Array.isArray(payload?.data) ? payload.data : [];
}

export async function getQuotationToolQuotations() {
  const payload = await requestAdminApi("/api/admin/quotation-tool-quotations");
  return Array.isArray(payload?.data) ? payload.data : [];
}

export async function getFurnitureEnquiry(id) {
  const payload = await requestAdminApi(`/api/admin/furniture-enquiries/${encodeURIComponent(id)}`);
  return payload?.data || null;
}

export async function updateFurnitureEnquiryStatus(id, status) {
  return requestAdminApi(`/api/admin/furniture-enquiries/${encodeURIComponent(id)}/status`, {
    method: "PATCH",
    body: JSON.stringify({ status }),
  });
}

export async function downloadFurnitureFloorplan(id) {
  const token = getAdminToken();
  const response = await fetch(`${API_BASE_URL}/api/admin/furniture-enquiries/${encodeURIComponent(id)}/floorplan`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    let payload;
    try { payload = await response.json(); } catch (error) { payload = null; }
    throw new Error(payload?.message || "Unable to download floor plan.");
  }
  const url = URL.createObjectURL(await response.blob());
  const link = document.createElement("a");
  link.href = url;
  link.download = "furniture-floorplan";
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export async function getQuoteToolConfig() {
  const payload = await requestAdminApi("/api/quote-tool/admin/config");
  return payload?.data || { packages: [], items: [], settings: {} };
}

export function saveQuoteToolPricing({ packages, items, settings, roomAmounts }) {
  return requestAdminApi("/api/quote-tool/admin/pricing", {
    method: "PUT",
    body: JSON.stringify({ packages, items, settings, roomAmounts }),
  });
}

export async function uploadQuoteToolItemImage(itemKey, image) {
  const data = new FormData();
  data.append("image", image);
  const response = await fetch(
    API_BASE_URL + "/api/quote-tool/admin/items/" + encodeURIComponent(itemKey) + "/image",
    { method: "POST", headers: { Authorization: "Bearer " + getAdminToken() }, body: data },
  );
  const payload = await response.json().catch(() => null);
  if (!response.ok) throw new Error(payload?.message || "Unable to upload quotation item image.");
  return payload;
}
